import { useCategoryItemContext } from "../../../../../../services/context/CategoryItemContext";
import { useAppSelector } from "../../../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../../../services/hooks/useTranslationI18";
import { OrderTypesEnum } from "../../../../../../utils/enums";
import { reCheckItemDetails } from "../../../../../../utils/help/itemDetails/itemDetails";
import { isValidOffer } from "../../../../../../utils/help/offers/offers";
import { ItemOfferV1Type, OFFER_TYPE } from "../../../../../../utils/types";
import { DeliveryTimeIcon } from "../../CategoryItemList.icons";
import styles from "./LabelV2.module.scss";
import { selectMaxEstimatedTime } from "../../../../../../services/redux/selectors/deliverySelectors";

export const LabelV2 = () => {
  const { item, isMenuView } = useCategoryItemContext();
  const { t } = useTranslationI18();
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );

  const hasRoundingMethodAndLevel =
    !!restaurantInfo?.currencyRoundingMethod &&
    !!restaurantInfo?.currencyRoundingLevel;
  const maxEstimatedDeliveryTime = useAppSelector(selectMaxEstimatedTime);
  const itemStatusRedux = useAppSelector((state) => state.itemStatus);
  const offers = useAppSelector(
    (state) => state.cartAndLoyaltyReducer.offersArray
  );

  // Offer cards carry no stock/availability label.
  if (!item) {
    return null;
  }

  const details = reCheckItemDetails(
    itemStatusRedux,
    item,
    undefined,
    hasRoundingMethodAndLevel
  );
  const isOutOfStock = details?.isOutOfStock || false;
  const itemDetails = details?.item;
  const customDeliveryTime = details.customDeliveryTime;

  const outOfStockText = t("offers:out-of-stock");
  const minsText = t("common:mins");
  const offersDontApplyText = t("common:offers-dont-apply");

  const isItemAvailable = item.timeBoundAvailabilityData.isAvailable;
  const availabilityMessage =
    item.timeBoundAvailabilityData.availabilityMessage;
  const isValidItemOffer = isValidOffer(itemDetails?.offer as ItemOfferV1Type);
  const showCustomDeliveryTime =
    customDeliveryTime &&
    orderTypeParam === OrderTypesEnum.DELIVERYV2 &&
    customDeliveryTime > maxEstimatedDeliveryTime;
  const showExcludeOffers = itemDetails.excludeFromOffers && offers.length > 0;

  const buildClassName = (...classes: (string | false | undefined)[]) =>
    classes.filter((c): c is string => Boolean(c)).join(" ");

  let labelClassName: string;
  let labelChildren: React.ReactNode;
  let dataTestId: string;

  const grid = isMenuView;

  if (isOutOfStock) {
    labelClassName = buildClassName(styles.label_red);
    labelChildren = outOfStockText;
    dataTestId = `outOfStock-item-${itemDetails.id}`;
  } else if (!isItemAvailable) {
    labelClassName = buildClassName(styles.label_primary);
    labelChildren = availabilityMessage;
    dataTestId = `outOfStock-item-${itemDetails.id}`;
  } else if (isValidItemOffer) {
    labelClassName = buildClassName(styles.label_offer, styles.label_primary);
    labelChildren = `${itemDetails.offer?.offerValue}${
      itemDetails.offer?.offerType === OFFER_TYPE.PERCENTAGE ? "%" : ""
    }`;
    dataTestId = `offer-item-${itemDetails.id}`;
  } else if (showCustomDeliveryTime) {
    labelClassName = buildClassName(styles.label_primary);
    labelChildren = (
      <>
        <DeliveryTimeIcon />
        {`${customDeliveryTime} ${minsText}`}
      </>
    );
    dataTestId = `custom-delivery-time-item-${itemDetails.id}`;
  } else if (showExcludeOffers) {
    labelClassName = buildClassName(
      styles.label_loyalty,
      !grid && styles.label_smaller
    );
    labelChildren = offersDontApplyText;
    dataTestId = `exclude_from_offers-${itemDetails.id}`;
  } else {
    return null;
  }

  return (
    <span
      className={`${styles.label} ${
        grid ? styles.label_grid : styles.label_top
      } ${labelClassName}`}
      data-testid={dataTestId}
    >
      {labelChildren}
    </span>
  );
};
